import mockAccountingReportsData from "../data/mockData/accountingReportsData";
import mockArtistWishlistData from "../data/mockData/artistWishlistData";
import mockArtistsData from "../data/mockData/artistsData";
import { getArtistRows } from "./artistDemoHelpers";
import { getLabelRows, enrichLabelRow } from "./labelDemoHelpers";
import {
  findArtistWishlistIdBySpotifyIdentifier,
  getArtistWishlistById,
  getArtistWishlistRows,
} from "./artistWishlistDemoHelpers";
import mockAudioMasteringData from "../data/mockData/audioMasteringData";
import mockEarningsFilterOptionsData from "../data/mockData/earningsFilterOptionsData";
import mockFeatureRequestsData from "../data/mockData/featureRequestsData";
import ddexStoresData from "../data/mockData/ddexStoresData";
import mockGenresData from "../data/mockData/genresData";
import mockInvoicesData from "../data/mockData/invoicesData";
import mockLabelsData from "../data/mockData/labelsData";
import mockLinkLandingPagesData from "../data/mockData/linkLandingPagesData";
import mockMusicLinksData from "../data/mockData/musicLinksData";
import mlPremadeColorsData from "../data/mockData/mlPremadeColorsData";
import mockMusicLinkUrlPartsData from "../data/mockData/musicLinkUrlPartsData";
import mockPaymentMethodsData from "../data/mockData/paymentMethodsData";
import mockPromotionsData from "../data/mockData/promotionsData";
import mockPurchaseHistoryData from "../data/mockData/purchaseHistoryData";
import mockRecipientsData from "../data/mockData/recipientsData";
import mockRecipientListsData from "../data/mockData/recipientListsData";
import mockReleaseData from "../data/mockData/releasesData";
import mockReleaseDetailsData from "../data/mockData/releaseDetailsData";
import mockDisputeClaimsData from "../data/mockData/disputeClaimsData";
import mockStatementsData from "../data/mockData/statementsData";
import moment from "moment";
import simulateMockEarningsData from "../data/mockData/generateRandomEarningsData";
import simulateMockTrendsData from "../data/mockData/generateRandomTrendsData";
import {
  mockContributorsData,
  mockPublishersData,
  mockShareholdersData,
  mockTrackCostsData,
} from "../data/mockData/trackMetadataData";
import {
  mockAnnouncementsData,
  mockBrandingConfigData,
  mockCurrentUserData,
  mockDeploymentCreditsData,
  mockEmployeeAccountsData,
  mockInvoiceConfigurationData,
  mockSlaPlansData,
  mockSubuserInvoicesData,
  mockSubUsersData,
  mockTaxRatesData,
  mockTenantData,
  mockTwoFactorData,
} from "../data/mockData/wlDemoData";
import {
  getDemoStripeConfig,
  getDemoStripeProduct,
  listDemoPublicPlans,
  listDemoStripeProducts,
} from "./stripeSubscriptionDemoHelpers";
import { isBrowserSessionDemo, isDemoMode } from "./demoConfig";
import { getDemoTwoFactorStatus, getMergedDemoUser } from "./demoTwoFactorHelpers";
import {
  getBrowserSessionGetResponse,
  getDeploymentCreditRows,
  getMergedWlDemoTenantSnapshot,
} from "./browserSessionDemoApi";
import {
  getAccountingReportRows,
  resolveAccountingOverview,
} from "./accountingDemoHelpers";
import { getInvoiceRows } from "./invoiceDemoHelpers";
import {
  resolveStatementsList,
  resolveSubuserStatementsList,
} from "./subuserStatementDemoHelpers";
import { getSubUserRows } from "./subUserDemoHelpers";
import {
  getTenantInvoiceConfig,
  resolveSubuserInvoicesList,
  resolveTaxRatesList,
} from "./subuserInvoiceDemoHelpers";
import { normalizeUrl, paginateRows, getDetail, parseSubUserIdFromPath } from "./listQueryUtils";
import { applySubUserSeedToRows } from "../data/mockData/subUserSeedAssignments";
import { getActiveDemoLandingSeedRows, getDemoLandingRows } from "./demoLinkLandingSessionStore";
import {
  buildAnnouncementsOverview,
  getMergedAnnouncements,
} from "./demoAnnouncementsSessionStore";
import {
  enrichMusicLinkDetail,
  enrichMusicLinkListRow,
  findMusicLinkByPublicParts,
  getMusicLinkById,
  getMusicLinkRows,
} from "./musicLinkDemoHelpers";
import {
  enrichMusicLinkUrlPartRow,
  getMusicLinkUrlPartById,
  getMusicLinkUrlPartRows,
  validateMusicLinkUrlParts,
} from "./musicLinkUrlPartsDemoHelpers";
import { enrichReleaseEstimatedDelivery } from "./estimatedDeliveryHelpers";
import { resolveDemoReleaseStoreUrls } from "./demoReleaseStoreUrlHelpers";
import {
  resolveDeliveryConfirmationDetail,
  resolveDeliveryConfirmationList,
} from "./deliveryConfirmationDemoHelpers";

/** API returns nested artwork; mock rows often use a single image URL string. */
function normalizeAccountingReportRow(row) {
  if (!row || typeof row !== "object") {
    return row;
  }
  const art = row.release_artwork;
  if (art != null && typeof art === "string") {
    return {
      ...row,
      release_artwork: {
        thumb_small: art,
        thumb_medium: art,
      },
    };
  }
  return row;
}

function normalizeAccountingReportsShape(data) {
  if (!data || typeof data !== "object") {
    return data;
  }
  if (Array.isArray(data.results)) {
    return {
      ...data,
      results: data.results.map(normalizeAccountingReportRow),
    };
  }
  if (data.id != null) {
    return normalizeAccountingReportRow(data);
  }
  return data;
}

function normalizeReleaseArtworkShape(data) {
  if (!data || typeof data !== "object" || Array.isArray(data.results)) {
    return data;
  }
  const art = data.artwork;
  if (art != null && typeof art === "string") {
    return {
      ...data,
      artwork: {
        thumb_medium: art,
        thumb_small: art,
      },
    };
  }
  return data;
}

function getTrackByIdFromReleaseDetails(id) {
  const sid = String(id);
  for (let i = 0; i < mockReleaseDetailsData.length; i += 1) {
    const release = mockReleaseDetailsData[i];
    const tracks = Array.isArray(release?.tracks) ? release.tracks : [];
    const hit = tracks.find(track => String(track.id) === sid);
    if (hit) {
      return hit;
    }
  }
  return { detail: "Not found." };
}

function enrichReleaseListRow(row, slaLevel) {
  const detail = getDetail(mockReleaseDetailsData, row?.id);
  const withStoreUrls = {
    ...row,
    ...(detail?.kind != null ? { kind: detail.kind } : {}),
    store_urls: resolveDemoReleaseStoreUrls(row),
  };
  return enrichReleaseEstimatedDelivery(withStoreUrls, slaLevel);
}

function paginateSeededCollection(collection, rows, segments, queryParams) {
  const subUserIdFromPath = parseSubUserIdFromPath(segments);
  const seeded = applySubUserSeedToRows(collection, rows);
  return paginateRows(seeded, queryParams, { subUserIdFromPath });
}

function seedCollectionRow(collection, row) {
  if (!row || typeof row !== "object") {
    return row;
  }
  const [seeded] = applySubUserSeedToRows(collection, [row]);
  return seeded || row;
}

function mergeById(baseRows, extraRows) {
  const normalizeLandingOverlay = (baseRow, extraRow) => {
    if (!baseRow) {
      return extraRow;
    }
    const merged = { ...baseRow, ...extraRow };
    if (
      Array.isArray(baseRow.social_links) &&
      baseRow.social_links.length > 0 &&
      (!Array.isArray(extraRow?.social_links) || extraRow.social_links.length === 0)
    ) {
      merged.social_links = baseRow.social_links;
    }
    if (
      Array.isArray(baseRow.featured_links) &&
      baseRow.featured_links.length > 0 &&
      (!Array.isArray(extraRow?.featured_links) || extraRow.featured_links.length === 0)
    ) {
      merged.featured_links = baseRow.featured_links;
    }
    return merged;
  };

  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      const existing = map.get(String(row.id));
      map.set(String(row.id), normalizeLandingOverlay(existing, row));
    }
  });
  return Array.from(map.values());
}

const appVariables = {
  "terms-of-distribution-url": "https://example-label.test/demo/terms-of-distribution",
  "terms-of-use-url": "https://example-label.test/demo/terms-of-use",
  "privacy-policy-url": "https://example-label.test/demo/privacy-policy",
};

const colorPresets = mlPremadeColorsData;

const roleData = [
  { id: 1, name: "Composer" },
  { id: 2, name: "Lyricist" },
  { id: 3, name: "Producer" },
];

const endpointData = {
  "accounting_reports": mockAccountingReportsData,
  "artists": mockArtistsData,
  "artist-wishlist": mockArtistWishlistData,
  "audio-masterings": mockAudioMasteringData,
  "announcements": mockAnnouncementsData,
  "copyright-infringements": mockDisputeClaimsData,
  "dispute-claims": mockDisputeClaimsData,
  "deployment-credits": mockDeploymentCreditsData,
  "employee-accounts": mockEmployeeAccountsData,
  "feature-request": mockFeatureRequestsData,
  "genres": mockGenresData,
  "invoices": mockInvoicesData,
  "labels": mockLabelsData,
  "link-landingpages": mockLinkLandingPagesData,
  "musiclinks": mockMusicLinksData,
  "musiclinks_urlparts": mockMusicLinkUrlPartsData,
  "payment-methods": mockPaymentMethodsData,
  "promotions": mockPromotionsData,
  "purchase-history": mockPurchaseHistoryData,
  "recipient-lists": mockRecipientListsData,
  "recipients": mockRecipientsData,
  "contributors": mockContributorsData,
  "publishers": mockPublishersData,
  "shareholders": mockShareholdersData,
  "track-costs": mockTrackCostsData,
  "releases": mockReleaseData,
  "statements": mockStatementsData,
  "sub-users": mockSubUsersData,
  "subuser-invoices": mockSubuserInvoicesData,
  "subuser-statements": mockStatementsData,
  "tax-rates": mockTaxRatesData,
  "user-payment/list": mockPaymentMethodsData,
};

const directEndpointData = {
  "app-variables": appVariables,
  "announcements/overview": {
    count: mockAnnouncementsData.length,
    unread_count: mockAnnouncementsData.filter(a => !a.is_read).length,
    results: mockAnnouncementsData,
  },
  "contributor-roles": roleData,
  "ddex-store": ddexStoresData,
  "earnings/filter-options": mockEarningsFilterOptionsData,
  "invoice-configuration": mockInvoiceConfigurationData,
  "link-landingpages/available-domains": [{ id: 1, domain: "demo.example-label.test" }],
  "ml-premade-colors": colorPresets,
  "premade-colors": colorPresets,
  "shareholder-roles": roleData,
  "tenants": { count: 1, results: [mockTenantData] },
  "users": { count: 1, results: [mockCurrentUserData] }, // overridden in getMockResponse when demo
  "users/get-iam-credentials": {
    access_key_id: "DEMO_ACCESS_KEY",
    secret_access_key: "DEMO_SECRET_KEY",
    bucket: "demo-distribution-bucket",
  },
  "wl-sla-plans": mockSlaPlansData,
  "2fa": mockTwoFactorData, // overridden in getMockResponse when demo
};

function getQueryParam(queryParams, key) {
  if (!queryParams) {
    return null;
  }
  if (typeof queryParams.get === "function") {
    const value = queryParams.get(key);
    return value == null || value === "" ? null : value;
  }
  const value = queryParams[key];
  return value == null || value === "" ? null : value;
}

function parseMonthBoundToMoment(value, fallback) {
  if (!value || typeof value !== "string") {
    return fallback.clone();
  }
  const parsed = moment(value, "MM-YYYY", true);
  if (!parsed.isValid()) {
    return fallback.clone();
  }
  return parsed.startOf("month");
}

function resolveEarningsSimulationData(queryParams) {
  const defaultEnd = moment().subtract(4, "month").startOf("month");
  const defaultStart = moment().subtract(15, "month").startOf("month");
  const start = parseMonthBoundToMoment(
    getQueryParam(queryParams, "month_lower_bound"),
    defaultStart,
  );
  const endCandidate = parseMonthBoundToMoment(
    getQueryParam(queryParams, "month_upper_bound"),
    defaultEnd,
  );
  const end = endCandidate.isBefore(start) ? start.clone() : endCandidate;
  const filters = {
    store: getQueryParam(queryParams, "store"),
    label: getQueryParam(queryParams, "label"),
    release: getQueryParam(queryParams, "release"),
    track: getQueryParam(queryParams, "track"),
    country: getQueryParam(queryParams, "country"),
    subuser: getQueryParam(queryParams, "subuser"),
  };
  return simulateMockEarningsData(start, end, filters);
}

function resolveTrendsDayWindow(periodValue) {
  const map = {
    "1": 7,
    "2": 14,
    "3": 30,
    "4": 45,
    "5": 60,
    "6": 75,
    "7": 90,
  };
  return map[String(periodValue)] || 7;
}

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function withPercent(rows, valueKey, percentKey) {
  const total = rows.reduce((sum, row) => sum + (Number(row[valueKey]) || 0), 0) || 1;
  return rows.map(row => ({
    ...row,
    [percentKey]: Math.round(((Number(row[valueKey]) || 0) / total) * 100),
  }));
}

function makeLinkClickRows(items, labelFallback) {
  const rows = (items && items.length ? items : [{ title: labelFallback, platform: labelFallback, destination_url: "https://example.com" }])
    .slice(0, 5)
    .map(item => ({
      ...item,
      times_clicked: randomInt(20, 420),
    }))
    .sort((a, b) => b.times_clicked - a.times_clicked);
  return withPercent(rows, "times_clicked", "times_clicked_pct");
}

function getLinkLandingRows() {
  return mergeById(getActiveDemoLandingSeedRows(mockLinkLandingPagesData), getDemoLandingRows());
}

function simulateMusicLinkStats(queryParams) {
  const id = queryParams?.get("music_link_id");
  const musicLink = enrichMusicLinkDetail(
    getMusicLinkRows().find(row => String(row.id) === String(id)) || {},
  );

  const storeRows = [
    "spotify",
    "applemusic",
    "amazonmusic",
    "deezer",
    "youtube",
    "beatport",
    "tidal",
    "soundcloud",
    "other_stores",
  ]
    .map(storeName => ({
      store_name: storeName,
      total_visits: randomInt(40, 520),
    }))
    .sort((a, b) => b.total_visits - a.total_visits);

  const trackRows = (musicLink.preview_files?.length
    ? musicLink.preview_files
    : [{ track_name: musicLink.release_name || "Track 1", artist_name: musicLink.artist_name || "Artist" }]
  )
    .slice(0, 5)
    .map(track => ({
      track_name: track.track_name || musicLink.release_name || "Track",
      artist_name: track.artist_name || musicLink.artist_name || "Artist",
      release_image: musicLink.image_small || musicLink.image || "",
      total_streams: randomInt(30, 480),
    }))
    .sort((a, b) => b.total_streams - a.total_streams);

  const topTerritories = withPercent(
    [
      { territory: "United States", iso_code: "US", total_views: randomInt(180, 620) },
      { territory: "United Kingdom", iso_code: "GB", total_views: randomInt(120, 480) },
      { territory: "Germany", iso_code: "DE", total_views: randomInt(90, 360) },
      { territory: "Netherlands", iso_code: "NL", total_views: randomInt(70, 280) },
      { territory: "Other Territories", iso_code: "OT", total_views: randomInt(40, 180) },
    ].sort((a, b) => b.total_views - a.total_views),
    "total_views",
    "total_views_pct",
  );

  const topBrowsers = withPercent(
    [
      { browser: "Chrome", total_views: randomInt(220, 720) },
      { browser: "Safari", total_views: randomInt(120, 520) },
      { browser: "Firefox", total_views: randomInt(60, 260) },
      { browser: "Edge", total_views: randomInt(40, 220) },
      { browser: "Other Browser", total_views: randomInt(20, 120) },
    ].sort((a, b) => b.total_views - a.total_views),
    "total_views",
    "total_views_pct",
  );

  return {
    top_stores: withPercent(storeRows, "total_visits", "total_visits_pct"),
    top_tracks: withPercent(trackRows, "total_streams", "total_streams_pct"),
    top_territories: topTerritories,
    top_os: {
      labels: ["iOS", "Android", "macOS", "Windows", "Other"],
      data: [randomInt(20, 38), randomInt(18, 34), randomInt(12, 28), randomInt(10, 26), randomInt(3, 12)],
    },
    top_browsers: topBrowsers,
    top_device_categories: {
      labels: ["Mobile", "Desktop", "Tablet"],
      data: [randomInt(45, 70), randomInt(20, 45), randomInt(5, 15)],
      colors: ["#b40bbe", "#7460ee", "#26c6da"],
    },
  };
}

function simulateLinkLandingStats(queryParams) {
  const id = queryParams?.get("link_landing_page_id");
  const days = resolveTrendsDayWindow(queryParams?.get("period"));
  const landingPage = getLinkLandingRows().find(row => String(row.id) === String(id)) || {};
  const labels = [];
  const data = [];

  for (let index = days - 1; index >= 0; index -= 1) {
    labels.push(moment().subtract(index, "days").format("MMM D"));
    data.push(randomInt(25, 280));
  }

  const topTerritories = withPercent([
    { territory: "United States", iso_code: "US", total_views: randomInt(400, 1200) },
    { territory: "United Kingdom", iso_code: "GB", total_views: randomInt(220, 850) },
    { territory: "Germany", iso_code: "DE", total_views: randomInt(180, 650) },
    { territory: "Netherlands", iso_code: "NL", total_views: randomInt(100, 420) },
    { territory: "Other Territories", iso_code: "OT", total_views: randomInt(80, 360) },
  ].sort((a, b) => b.total_views - a.total_views), "total_views", "total_views_pct");

  const topBrowsers = withPercent([
    { browser: "Chrome", total_views: randomInt(500, 1400) },
    { browser: "Safari", total_views: randomInt(220, 900) },
    { browser: "Firefox", total_views: randomInt(90, 420) },
    { browser: "Edge", total_views: randomInt(60, 320) },
    { browser: "Other Browser", total_views: randomInt(40, 180) },
  ].sort((a, b) => b.total_views - a.total_views), "total_views", "total_views_pct");

  const socialRows = makeLinkClickRows(landingPage.social_links, "Instagram");
  const featuredRows = makeLinkClickRows(landingPage.featured_links, "Featured Link");

  return {
    total_page_views: data.reduce((sum, value) => sum + value, 0),
    total_social_link_clicks: socialRows.reduce((sum, row) => sum + row.times_clicked, 0),
    total_featured_link_clicks: featuredRows.reduce((sum, row) => sum + row.times_clicked, 0),
    date_wise_page_views: { labels, data },
    top_territories: topTerritories,
    top_os: {
      labels: ["iOS", "Android", "macOS", "Windows", "Other"],
      data: [randomInt(20, 38), randomInt(18, 34), randomInt(12, 28), randomInt(10, 26), randomInt(3, 12)],
    },
    top_browsers: topBrowsers,
    top_device_categories: {
      labels: ["Mobile", "Desktop", "Tablet"],
      data: [randomInt(45, 70), randomInt(20, 45), randomInt(5, 15)],
      colors: ["#b40bbe", "#7460ee", "#26c6da"],
    },
    social_link_stats: socialRows,
    featured_link_stats: featuredRows,
  };
}

export const getMockResponse = url => {
  if (isBrowserSessionDemo()) {
    const browserResponse = getBrowserSessionGetResponse(url);
    if (browserResponse !== undefined) {
      return normalizeReleaseArtworkShape(browserResponse);
    }
  }

  const { path, queryParams } = normalizeUrl(url);

  if ((path === "tenants" || path === "tenants/") && isDemoMode()) {
    const merged = getMergedWlDemoTenantSnapshot();
    return { count: 1, results: [merged] };
  }

  if (path === "earnings/accounting-overview") {
    return resolveAccountingOverview(queryParams);
  }

  if (path === "users" || path === "users/") {
    return { count: 1, results: [getMergedDemoUser()] };
  }

  if (path === "2fa" || path === "2fa/") {
    return getDemoTwoFactorStatus();
  }

  if (path === "tenant-invoice-config") {
    return getTenantInvoiceConfig();
  }

  if (path === "tenant-stripe-config" || path === "tenant-stripe-config/") {
    return getDemoStripeConfig();
  }

  if (path === "tenant-stripe-products" || path === "tenant-stripe-products/") {
    const products = listDemoStripeProducts();
    return { count: products.length, results: products };
  }

  if (path.startsWith("tenant-stripe-products/")) {
    const segments = path.split("/").filter(Boolean);
    const productId = segments[1];
    if (productId && /^\d+$/.test(productId)) {
      const product = getDemoStripeProduct(productId);
      return product || { detail: "Not found." };
    }
  }

  if (path === "tenant-stripe-plans" || path === "tenant-stripe-plans/") {
    const plans = listDemoPublicPlans();
    return { count: plans.length, results: plans };
  }

  if (path === "announcements/overview" || path === "announcements/overview/") {
    return buildAnnouncementsOverview(mockAnnouncementsData);
  }

  if (path === "earnings") {
    return resolveEarningsSimulationData(queryParams || {});
  }

  if (path === "trends") {
    const dayWindow = resolveTrendsDayWindow(queryParams?.period);
    return simulateMockTrendsData(dayWindow, {
      store: queryParams?.store || "0",
      label: queryParams?.label || "0",
      release: queryParams?.release || "0",
      track: queryParams?.track || "0",
      subuser: queryParams?.subuser || "0",
      period: queryParams?.period || "1",
    });
  }

  if (path === "ddex-delivery-confirmations" || path.startsWith("ddex-delivery-confirmations/")) {
    const segments = path.split("/").filter(Boolean);
    const detailId = segments.length >= 2 ? segments[1] : "";
    if (detailId && /^\d+$/.test(detailId)) {
      const detail = resolveDeliveryConfirmationDetail(detailId);
      return detail || { detail: "Not found." };
    }
    return resolveDeliveryConfirmationList(queryParams);
  }

  if (directEndpointData[path] !== undefined) {
    return directEndpointData[path];
  }



  if (path === "releases/get-release-details") {
    return normalizeReleaseArtworkShape(
      mockReleaseDetailsData[0] || mockReleaseData[0] || {},
    );
  }

  if (path === "link-landingpages/stats") {
    return simulateLinkLandingStats(queryParams);
  }

  if (path === "musiclinks/stats") {
    return simulateMusicLinkStats(queryParams);
  }

  if (path === "musiclinks_urlparts/validate-urlparts" || path.includes("validate-urlparts")) {
    const urlPart1 = queryParams?.get("url_part1");
    const urlPart2 = queryParams?.get("url_part2");
    return validateMusicLinkUrlParts(urlPart1, urlPart2);
  }

  if (path === "musiclink-landingpage") {
    const urlPart1 = String(queryParams?.get("url_part1") || "").trim();
    const urlPart2 = String(queryParams?.get("url_part2") || "").trim();
    if (!urlPart1 || !urlPart2) {
      return { detail: "Not found." };
    }
    const row = findMusicLinkByPublicParts(urlPart1, urlPart2);
    if (!row) {
      return { detail: "Not found." };
    }
    return enrichMusicLinkDetail(row);
  }

  if (path === "link-landingpage") {
    const backUrl = String(queryParams?.get("back_url") || "").trim().toLowerCase();
    if (!backUrl) {
      return { detail: "Not found." };
    }
    const mergedRows = getLinkLandingRows();
    const row = mergedRows.find(r => String(r?.back_url || "").trim().toLowerCase() === backUrl);
    if (!row) {
      return { detail: "Not found." };
    }
    return row;
  }

  if (path.startsWith("tracks/")) {
    const segments = path.split("/").filter(Boolean);
    if (segments.length >= 2) {
      return getTrackByIdFromReleaseDetails(segments[1]);
    }
  }

  const segments = path.split("/").filter(Boolean);
  const collection = segments[0];

  if (collection === "users") {
    const user = getMergedDemoUser();
    if (segments.length >= 2 && segments[1] && /^\d+$/.test(segments[1])) {
      if (String(user.id) === String(segments[1])) {
        return user;
      }
      return { detail: "Not found." };
    }
    return { count: 1, results: [user] };
  }

  const rows = endpointData[collection];
  const subUserIdFromPath = parseSubUserIdFromPath(segments);

  if (!rows) {
    return undefined;
  }

  const isSubUserScopedList = subUserIdFromPath != null;

  const effectiveRows =
    collection === "link-landingpages"
      ? getLinkLandingRows()
      : collection === "musiclinks"
        ? getMusicLinkRows().map(enrichMusicLinkListRow)
        : collection === "musiclinks_urlparts"
          ? getMusicLinkUrlPartRows().map(enrichMusicLinkUrlPartRow)
          : collection === "accounting_reports"
            ? getAccountingReportRows()
            : collection === "invoices"
              ? getInvoiceRows()
              : collection === "sub-users"
                ? getSubUserRows()
                : collection === "artists"
                ? getArtistRows()
                : collection === "artist-wishlist"
                  ? getArtistWishlistRows()
                  : collection === "labels"
                    ? getLabelRows()
                    : collection === "releases"
                      ? rows.map(row => enrichReleaseListRow(row, getMergedDemoUser()?.sla_level))
                      : collection === "deployment-credits"
                      ? getDeploymentCreditRows()
                      : collection === "announcements"
                        ? getMergedAnnouncements(mockAnnouncementsData)
                        : rows;

  if (segments.length > 1 && segments[1] && !isSubUserScopedList) {
    if (collection === "musiclinks" && segments[1] !== "stats") {
      return getMusicLinkById(segments[1]);
    }
    if (collection === "musiclinks_urlparts" && segments[1] !== "validate-urlparts") {
      return getMusicLinkUrlPartById(segments[1]);
    }
    if (collection === "artist-wishlist") {
      const wishlistDetail = getArtistWishlistById(segments[1]);
      if (wishlistDetail) {
        return wishlistDetail;
      }
    }
    const detail = getDetail(effectiveRows, segments[1]);
    if (collection === "releases") {
      const releaseDetail = getDetail(mockReleaseDetailsData, segments[1]) || detail;
      const listRow = getDetail(effectiveRows, segments[1]) || detail;
      const seededDetail = seedCollectionRow("releases", releaseDetail);
      return normalizeReleaseArtworkShape(
        enrichReleaseListRow(
          { ...listRow, ...seededDetail },
          getMergedDemoUser()?.sla_level,
        ),
      );
    }
    if (collection === "labels") {
      return enrichLabelRow(detail);
    }
    if (collection === "accounting_reports") {
      return normalizeAccountingReportsShape(detail);
    }
    return detail;
  }

  if (collection === "accounting_reports") {
    return normalizeAccountingReportsShape(paginateRows(effectiveRows, queryParams));
  }

  if (collection === "statements") {
    return resolveStatementsList(queryParams);
  }

  if (collection === "subuser-statements") {
    return resolveSubuserStatementsList(queryParams, subUserIdFromPath);
  }

  if (collection === "subuser-invoices") {
    return resolveSubuserInvoicesList(queryParams, subUserIdFromPath);
  }

  if (collection === "tax-rates") {
    return resolveTaxRatesList(queryParams);
  }

  return paginateSeededCollection(collection, effectiveRows, segments, queryParams);
};
